import type { ApiErrorBody, ErrorCode } from '@shared/errors';

/**
 * Typed API client.
 *
 * Every mutating request carries the CSRF header the Worker requires, cookies are sent
 * same-origin only, and error bodies are turned into `ApiError` so the UI can show the server's
 * message (and field-level errors) instead of a generic failure.
 */
export const CSRF_HEADER = 'X-Requested-With';
export const CSRF_VALUE = 'atlas';

export class ApiError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields: Record<string, string> | undefined;

  constructor(code: ErrorCode, message: string, status: number, fields?: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fields = fields;
  }

  /** True when the user simply needs to sign in again. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  json?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ Accept: 'application/json' });
  if (method !== 'GET') headers.set(CSRF_HEADER, CSRF_VALUE);
  if (options.json !== undefined) headers.set('Content-Type', 'application/json');

  const response = await fetch(path, {
    method,
    headers,
    credentials: 'same-origin',
    ...(options.json === undefined ? {} : { body: JSON.stringify(options.json) }),
    ...(options.signal ? { signal: options.signal } : {}),
  });

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const body = payload as ApiErrorBody | null;
    throw new ApiError(
      body?.error?.code ?? 'internal_error',
      body?.error?.message ?? `Request failed with status ${response.status}.`,
      response.status,
      body?.error?.fields,
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> => request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, json?: unknown): Promise<T> => request<T>(path, { method: 'POST', ...(json === undefined ? {} : { json }) }),
  patch: <T>(path: string, json?: unknown): Promise<T> => request<T>(path, { method: 'PATCH', ...(json === undefined ? {} : { json }) }),
  put: <T>(path: string, json?: unknown): Promise<T> => request<T>(path, { method: 'PUT', ...(json === undefined ? {} : { json }) }),
  delete: <T>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
};

/** WebSocket URL for live job progress (same origin, cookie-authenticated). */
export function socketUrl(path: string): string {
  const url = new URL(path, window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  return url.toString();
}
